import type { CSSProperties } from "react";
import type { KnCoat, KnHorseDef, KnSilk } from "@ziolo/shared";

const COAT: Record<KnCoat, { body: string; mane: string; sock: string }> = {
  chestnut: { body: "#9a4e28", mane: "#5c2410", sock: "#1a1a1a" },
  black: { body: "#1c1c1c", mane: "#0a0a0a", sock: "#111" },
  grey: { body: "#c8c8c8", mane: "#8d8d8d", sock: "#f5f5f5" },
  bay: { body: "#6b3a18", mane: "#1a1a1a", sock: "#111" },
  palomino: { body: "#e2c36a", mane: "#f4ead0", sock: "#fff" },
  brown: { body: "#7a4a28", mane: "#3d2210", sock: "#2b1508" },
};

function SilkMark({ silk, accent }: { silk: KnSilk; accent: string }) {
  if (silk === "bolt") {
    return <polygon points="22,4 28,4 25,10 30,10 20,22 23,13 18,13" fill={accent} />;
  }
  if (silk === "sash") {
    return <polygon points="14,2 24,2 36,24 26,24" fill={accent} opacity="0.9" />;
  }
  if (silk === "star") {
    return <polygon points="25,3 27,9 33,9 28,13 30,19 25,15 20,19 22,13 17,9 23,9" fill={accent} />;
  }
  if (silk === "stripe") {
    return (
      <>
        <rect x="20" y="2" width="4" height="22" fill={accent} />
        <rect x="28" y="2" width="4" height="22" fill={accent} opacity="0.85" />
      </>
    );
  }
  if (silk === "dot") {
    return (
      <>
        <circle cx="22" cy="8" r="2.2" fill={accent} />
        <circle cx="30" cy="8" r="2.2" fill={accent} />
        <circle cx="26" cy="15" r="2.2" fill={accent} />
        <circle cx="22" cy="20" r="2.2" fill={accent} />
        <circle cx="30" cy="20" r="2.2" fill={accent} />
      </>
    );
  }
  return (
    <>
      <rect x="16" y="4" width="6" height="6" fill={accent} />
      <rect x="28" y="4" width="6" height="6" fill={accent} />
      <rect x="22" y="10" width="6" height="6" fill={accent} />
      <rect x="16" y="16" width="6" height="6" fill={accent} />
      <rect x="28" y="16" width="6" height="6" fill={accent} />
    </>
  );
}

export function HorseSprite({
  horse,
  racing,
  winner,
  place,
}: {
  horse: KnHorseDef;
  racing: boolean;
  winner: boolean;
  place: number | null;
}) {
  const coat = COAT[horse.coat];
  const classes = [
    "kn-horse",
    racing ? "kn-horse--racing" : "",
    winner ? "kn-horse--winner" : "",
    place === 1 ? "kn-horse--first" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} style={{ "--kn-silk": horse.color } as CSSProperties}>
      {place !== null ? (
        <span className={`kn-horse__place kn-horse__place--${place}`}>{place}</span>
      ) : null}
      <svg className="kn-horse__art" viewBox="0 0 132 78" aria-hidden="true">
        <ellipse className="kn-horse__dust" cx="38" cy="70" rx="22" ry="5" fill="rgba(90,60,30,0.35)" />
        <g className="kn-legs">
          <g className="kn-leg kn-leg--back">
            <rect x="48" y="48" width="5" height="22" rx="2" fill={coat.body} />
            <rect x="47" y="68" width="8" height="4" rx="1" fill={coat.sock} />
          </g>
          <g className="kn-leg kn-leg--back2">
            <rect x="58" y="48" width="5" height="20" rx="2" fill={coat.mane} />
            <rect x="57" y="66" width="8" height="4" rx="1" fill={coat.sock} />
          </g>
          <g className="kn-leg kn-leg--front">
            <rect x="86" y="48" width="5" height="22" rx="2" fill={coat.body} />
            <rect x="85" y="68" width="8" height="4" rx="1" fill={coat.sock} />
          </g>
          <g className="kn-leg kn-leg--front2">
            <rect x="96" y="48" width="5" height="20" rx="2" fill={coat.mane} />
            <rect x="95" y="66" width="8" height="4" rx="1" fill={coat.sock} />
          </g>
        </g>
        <ellipse cx="72" cy="44" rx="32" ry="16" fill={coat.body} />
        <path d="M96 38 C108 28 114 22 118 18 C122 22 120 32 112 40 Z" fill={coat.body} />
        <ellipse cx="122" cy="20" rx="10" ry="7" fill={coat.body} />
        <polygon points="118,14 122,4 126,14" fill={coat.mane} />
        <path d="M40 40 C28 36 22 44 26 52 C34 48 42 48 46 46 Z" fill={coat.mane} />
        <ellipse cx="126" cy="21" rx="1.4" ry="1.8" fill="#111" />
        <g className="kn-jockey">
          <rect x="68" y="18" width="22" height="20" rx="4" fill={horse.color} />
          <g transform="translate(62 16)">
            <SilkMark silk={horse.silk} accent={horse.accent} />
          </g>
          <circle cx="86" cy="14" r="7" fill="#f0d0b0" />
          <path d="M79 10 H93 Q96 10 96 14 Q88 8 79 12 Z" fill={horse.accent} />
        </g>
        <g>
          <rect x="78" y="36" width="16" height="12" rx="2" fill={horse.color} stroke={horse.accent} strokeWidth="1.2" />
          <text
            x="86"
            y="46"
            textAnchor="middle"
            fontSize="9"
            fontWeight="700"
            fill={horse.accent}
            fontFamily="ui-monospace, monospace"
          >
            {horse.id}
          </text>
        </g>
      </svg>
      <span className="kn-horse__name">{horse.name}</span>
    </div>
  );
}
